import { captureException } from '@sentry/nextjs'

import {
  BootcampLevelArg,
  CategoriesData,
  SubjectDefaultName,
  VccData,
  LevelActionType,
} from '~/bootcamp/types/bootcamp.types'
import { getUsableVideoDescription } from '~/curriculum/utils/videoPoster.utils'
import { bootcampSanity } from '~/shared/sanity/bootcamp'
import { bootcampsListGroq, SubjectsListGroq } from '~/types/bootcamp'
import { AppLanguage } from '~/types/locale'

export const getSubjectsListWithCourses = async (
  language: AppLanguage,
  gymCategory: string,
): Promise<SubjectsListGroq> => {
  try {
    const subjectsListData = await bootcampSanity.listSubjectsWithCourses({ language, gymCategory })
    return subjectsListData
  } catch (error) {
    // Pass/Capture error log to sentry
    captureException(error, { tags: { feature: 'bootcamp', action: 'sanity-fetch' } })

    return {
      subjects: [],
      reference: [],
    }
  }
}
export const getFirstSubjectKey = async (
  language: AppLanguage,
  gymCategory: string,
): Promise<{ subjectKey: string }> => {
  try {
    const subjectKey = await bootcampSanity.firstSubjectKey({ language, gymCategory })
    return subjectKey
  } catch (error) {
    captureException(error, { tags: { feature: 'bootcamp', action: 'sanity-fetch' } })
    return {
      subjectKey: '',
    }
  }
}

export const getBootcampsList = async (language: AppLanguage, key?: string): Promise<bootcampsListGroq> => {
  const defaultBootcampsList: bootcampsListGroq = {
    bootcamps: {
      name: '',
      metaDescription: '',
    },
    reference: {
      modules: [],
    },
  }

  if (!key) return defaultBootcampsList

  try {
    const bootcampsList = await bootcampSanity.bootcampsList({ key, language })

    return bootcampsList || defaultBootcampsList
  } catch (error) {
    // Pass/Capture error log to sentry
    captureException(error, { tags: { feature: 'bootcamp', action: 'sanity-fetch' } })

    return defaultBootcampsList
  }
}

/** awsVideo fields the query projects; `VccData` types them as `unknown[]`. */
interface PosterVideoContent {
  thumbnailUrl?: string
  description?: string
}

/** Thumbnail key (link-validity check) plus poster headline, in one request. */
export const getVideoPosterInfo = async (args: BootcampLevelArg): Promise<{ thumbnailUrl: string; title: string }> => {
  const { type } = args
  try {
    const data = await bootcampSanity.getVccThumbnail(args)
    const { module } = data
    const page =
      type === LevelActionType.Why
        ? undefined
        : module?.page?.filter((item) => item.isDefinition === (type === LevelActionType.Definition))[0]
    const content = (type === LevelActionType.Why ? module?.howContent : page?.howContent) as
      | PosterVideoContent
      | undefined

    return {
      thumbnailUrl: content?.thumbnailUrl ?? '',
      title:
        getUsableVideoDescription(content?.description) ||
        (type === LevelActionType.Why ? module?.whyName ?? module?.name ?? '' : page?.name ?? ''),
    }
  } catch (error) {
    captureException(error, { tags: { feature: 'bootcamp', action: 'sanity-fetch' } })
    return { thumbnailUrl: '', title: '' }
  }
}

export const getVccData = async (args: BootcampLevelArg): Promise<VccData> => {
  try {
    const data = await bootcampSanity.getVccSanityData(args)
    const { name, module, moduleCount } = data
    return { name, module, moduleCount }
  } catch (error) {
    captureException(error, { tags: { feature: 'bootcamp', action: 'sanity-fetch' } })
    return {
      name: '',
      module: undefined,
      moduleCount: 0,
    }
  }
}

export const getBootcampCategoriesData = async (language: AppLanguage): Promise<CategoriesData | null> => {
  try {
    const numberOfGymsInCat = await bootcampSanity.getCategoriesData({ language })

    return numberOfGymsInCat
  } catch (error) {
    // Pass/Capture error log to sentry
    captureException(error, { tags: { feature: 'bootcamp', action: 'sanity-fetch' } })

    return null
  }
}

export const getSubjectAndCategoryName = async (
  language: AppLanguage,
  bootcampCategory: string,
  key: string,
): Promise<SubjectDefaultName | null> => {
  try {
    const data = await bootcampSanity.getSubjectAndCategoryName({ language, bootcampCategory, key })
    return data || null
  } catch (error) {
    // Pass/Capture error log to sentry
    captureException(error, { tags: { feature: 'bootcamp', action: 'sanity-fetch' } })
    return null
  }
}
